import { Button, DatePicker, Dropdown, Radio, Select, Divider, Badge, Tooltip, Space, ConfigProvider } from 'antd'
import { CalendarOutlined, FilterOutlined, DownOutlined, LeftOutlined, RightOutlined, ClockCircleOutlined, FlagOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDay, faCalendarWeek, faCalendar, faShareNodes } from '@fortawesome/pro-regular-svg-icons'
import { ScheduleFilters, ScheduleMode, ScheduleState, EventStatus, defaultScheduleFilters } from './scheduleModel'
import { Dayjs } from "dayjs"
import { useEffect, useState, useRef } from 'react'
import FiltersDrawer from './FiltersDrawer'
import EditVisitModal from '../visits/EditVisitModal'
import ShareCalendarModal from './ShareCalendarModal'
import { useSchedule } from './ScheduleContext'
import './ScheduleTitleBar.css'
import TaskEventModal from './EditTask/TaskEventModal'
import CrewFilter from './CrewFilter'
import CalendarSearch from './CalendarSearch'

type Props = {
  state: ScheduleState
  onViewChange: (view: ScheduleMode) => void
  onTodayClick: () => void
  onPrevClick: () => void
  onNextClick: () => void
  onDateChange: (date: Dayjs) => void
  onFiltersChange: (filters: ScheduleFilters) => void
  onOpenCreateEventDrawer: (tab?: string) => void
}

const countActiveFilters = (filters: ScheduleFilters): number => {
  let count = 0
  if (filters.crewMemberIds.length > 0) { count += 1 }
  if (filters.divisionIds.length > 0) { count += 1 }
  if (filters.projectTypeIds.length > 0) { count += 1 }
  if (filters.salesPersonIds.length > 0) { count += 1 }
  if (filters.eventStatuses.length !== defaultScheduleFilters.eventStatuses.length ||
    !filters.eventStatuses.every(s => defaultScheduleFilters.eventStatuses.includes(s))) {
    count += 1
  }
  if (filters.eventTypes.length > 0) { count += 1 }
  if (filters.visitStatuses.length > 0) { count += 1 }
  if (filters.missingVisits) { count += 1 }
  return count
}

const ScheduleTitleBar = ({ state, onViewChange, onTodayClick, onPrevClick, onNextClick, onDateChange, onFiltersChange, onOpenCreateEventDrawer }: Props) => {
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)
  const [editVisitModalOpen, setEditVisitModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const { crew } = useSchedule()

  const activeFiltersCount = countActiveFilters(state.filters)

  const toggleCrewLeadFilter = (crewMemberId: string) => {
    const currentIds = state.filters.crewMemberIds
    const newIds = currentIds.includes(crewMemberId)
      ? currentIds.filter(id => id !== crewMemberId)
      : [...currentIds, crewMemberId]
    onFiltersChange({ ...state.filters, crewMemberIds: newIds })
  }

  const toggleEventStatusFilter = (status: EventStatus) => {
    const currentStatuses = state.filters.eventStatuses
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status]
    onFiltersChange({ ...state.filters, eventStatuses: newStatuses })
  }

  const eventStatuses = [
    { status: EventStatus.Planned, label: 'Planned', color: '#1890ff', icon: <CalendarOutlined /> },
    { status: EventStatus.ClockedIn, label: 'Clocked-In', color: '#52c41a', icon: <ClockCircleOutlined /> },
    { status: EventStatus.Visited, label: 'Visited', color: '#8c8c8c', icon: <FlagOutlined /> },
  ]

  const viewOptions = [
    { value: 'day', label: 'Day', icon: faCalendarDay },
    { value: 'week', label: 'Week', icon: faCalendarWeek },
    { value: 'month', label: 'Month', icon: faCalendar },
  ]

  const titleBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = titleBarRef.current
    if (!el) {
      return
    }
    const update = () => {
      el.parentElement?.style.setProperty('--title-bar-height', `${el.offsetHeight}px`)
    }
    const observer = new ResizeObserver(update)
    observer.observe(el)
    update()
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={titleBarRef} style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      backgroundColor: 'white',
      borderBottom: '1px solid #f0f0f0',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <Radio.Group
        value={state.view}
        onChange={(e) => onViewChange(e.target.value as ScheduleMode)}
        id="calendar-mode-switcher"
        className="schedule-title-bar__mode-switcher"
      >
        {viewOptions.map(opt => (
          <Radio.Button key={opt.value} value={opt.value} style={{ fontWeight: 'bold' }} aria-label={opt.label}>
            <Space>
              <FontAwesomeIcon icon={opt.icon} />
              <span className="schedule-title-bar__mode-label">{opt.label}</span>
            </Space>
          </Radio.Button>
        ))}
      </Radio.Group>

      <span id="calendar-mode-select" className="schedule-title-bar__mode-select">
        <Select
          value={state.view}
          onChange={(value) => onViewChange(value as ScheduleMode)}
          options={viewOptions.map(opt => ({
            value: opt.value,
            label: <Space><FontAwesomeIcon icon={opt.icon} />{opt.label}</Space>
          }))}
          style={{ width: 110 }}
        />
      </span>

      <Button type="default" style={{ fontWeight: 'bold' }} className="schedule-title-bar__today-button" onClick={onTodayClick}>Today</Button>

      <Space.Compact>
        <Button onClick={onPrevClick}><LeftOutlined /></Button>
        <Button onClick={onNextClick}><RightOutlined /></Button>
      </Space.Compact>

      <DatePicker
        value={state.selectedDate}
        onChange={(value) => {
          if (value) {
            onDateChange(value)
          }
        }}
        format="ddd, MMM D"
        suffixIcon={<DownOutlined />}
        style={{ width: 140, minWidth: 140, fontWeight: 'bold' }}
        variant="outlined"
        allowClear={false}
      />

      <CalendarSearch
        onSelectDate={onDateChange}
        selectedDate={state.selectedDate}
        visibleDateRange={state.visibleDateRange}
      />

      <Divider type="vertical" style={{ height: '24px' }} className="schedule-title-bar__crew-divider" />

      <CrewFilter
        crew={crew}
        selectedCrewMemberIds={state.filters.crewMemberIds}
        onToggleCrewMember={toggleCrewLeadFilter}
        onClearCrewFilter={() => onFiltersChange({ ...state.filters, crewMemberIds: [] })}
      />

      <Divider type="vertical" style={{ height: '24px' }} className="schedule-title-bar__status-divider" />

      <div style={{ display: 'flex', alignItems: 'center' }} className="schedule-title-bar__status-controls" id="calendar-status-filter">
        {eventStatuses.map((config, index) => {
          const isSelected = state.filters.eventStatuses.includes(config.status)
          return (
            <Tooltip key={config.status} title={config.label}>
              <div
                onClick={() => toggleEventStatusFilter(config.status)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '50%',
                  border: isSelected ? '2px solid #faad14' : '2px solid transparent',
                  boxShadow: isSelected ? '0 0 0 2px rgba(250, 173, 20, 0.3)' : undefined,
                  marginLeft: index > 0 ? '-8px' : '0',
                  position: 'relative',
                  zIndex: isSelected ? 10 : 1,
                  transition: 'z-index 0s, transform 0.1s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.zIndex = '20'; e.currentTarget.style.transform = 'scale(1.1)' }}
                onMouseLeave={(e) => { e.currentTarget.style.zIndex = isSelected ? '10' : '1'; e.currentTarget.style.transform = 'scale(1)' }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#d9d9d9',
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '3px solid #d9d9d9',
                  position: 'relative',
                  fontSize: '18px',
                  color: '#595959'
                }}>
                  {config.icon}
                  <div style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: config.color,
                    borderRadius: '50%',
                    border: '2px solid white',
                  }} />
                </div>
              </div>
            </Tooltip>
          )
        })}
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>

        <Divider type="vertical" style={{ height: '24px' }} />
        <Button
          icon={<FontAwesomeIcon icon={faShareNodes} />}
          type="default"
          style={{ fontWeight: 'bold' }}
          onClick={() => setShareModalOpen(true)}
        >
          Share
        </Button>

        <Divider type="vertical" style={{ height: '24px' }} />
        <Badge count={activeFiltersCount} offset={[10, 0]} id="calendar-filter-button">
          <Space.Compact>
            <Button
              icon={<FilterOutlined />}
              type="default"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Filters
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'clear-filters',
                    label: 'Clear Filters',
                    disabled: activeFiltersCount === 0,
                    onClick: () => {
                      onFiltersChange(defaultScheduleFilters)
                    }
                  },
                ]
              }}
            >
              <Button type="default" icon={<DownOutlined />} />
            </Dropdown>
          </Space.Compact>
        </Badge>

        <Divider type="vertical" style={{ height: '24px' }} />

        <ConfigProvider theme={{
          token: {
            colorPrimary: '#ff5c35'
          }
        }}>
          <Space.Compact id="create-event-button">
            <Button
              type="primary"
              onClick={() => {
                onOpenCreateEventDrawer('1')
              }}
            >
              Add to Schedule
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'schedule-on-demand-route', label: 'Schedule On-Demand Route', onClick: () => {
                      onOpenCreateEventDrawer('2')
                    }
                  },
                  {
                    key: 'schedule-task', label: 'Schedule Task', onClick: () => {
                      setTaskModalOpen(true)
                    }
                  },
                  {
                    key: 'record-visit', label: 'Record Visit', onClick: () => {
                      setEditVisitModalOpen(true)
                    }
                  },
                ]
              }}
            >
              <Button type="primary" icon={<DownOutlined />} />
            </Dropdown>
          </Space.Compact>
        </ConfigProvider>
      </div>

      <FiltersDrawer
        open={filtersDrawerOpen}
        onClose={() => {
          setFiltersDrawerOpen(false)
        }}
        filters={state.filters}
        onFiltersChange={onFiltersChange}
      />

      <ShareCalendarModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} />

      <TaskEventModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        selectedDate={state.selectedDate}
      />

      {editVisitModalOpen && <EditVisitModal
        isOpen={editVisitModalOpen}
        visitId={''}
        onCloseModal={() => setEditVisitModalOpen(false)}
        googleMapsApiKey={(window as any).env.GOOGLE_MAPS_API_KEY}
        defaultLunchBreakMinutes={0}
      />}
    </div>
  )
}

export default ScheduleTitleBar